import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InteractiveCanvas from './components/InteractiveCanvas';
import InstinctQuote from './components/InstinctQuote';
import LipstickTryOn from './components/LipstickTryOn';
import HomeFooter from '@/pages/home/components/HomeFooter';

export default function Interactive() {
  const [entered, setEntered] = useState(false);
  const [introVisible, setIntroVisible] = useState(true);
  const [hint, setHint] = useState(true);

  useEffect(() => {
    if (!entered) return;
    const t = setTimeout(() => setHint(false), 5000);
    return () => clearTimeout(t);
  }, [entered]);

  const handleEnter = () => {
    setIntroVisible(false);
    setTimeout(() => setEntered(true), 700);
  };

  return (
    <>
      {/* ── IMMERSIVE CANVAS SECTION ── */}
      <div className="relative w-full h-screen overflow-hidden bg-black select-none">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/806163d612b995f0f2d7f5c0212b0f62.jpg"
            alt=""
            className="w-full h-full object-cover object-center"
            style={{ filter: 'brightness(0.35)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
        </div>

        {/* Interactive Canvas layer */}
        {entered && <InteractiveCanvas />}

        {/* ── INTRO SCREEN ── */}
        <div
          className="absolute inset-0 z-30 flex flex-col items-center justify-center transition-all duration-700"
          style={{
            opacity: introVisible ? 1 : 0,
            pointerEvents: introVisible ? 'auto' : 'none',
          }}
        >
          <p
            className="text-[#4D1815] text-xs mb-8"
            style={{ letterSpacing: '0.55em', fontFamily: "'Cormorant Garamond', serif" }}
          >
            — INTERACTIVE EXPERIENCE —
          </p>
          <h1
            className="text-[#EADDCC] font-light text-center leading-none mb-6"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: 'clamp(44px, 8vw, 110px)',
              letterSpacing: '0.1em',
            }}
          >
            FEEL THE
            <br />
            <em className="italic text-[#EADDCC]/60">INSTINCT</em>
          </h1>
          <div className="w-14 h-px bg-[#4D1815] mx-auto mb-8" />
          <p
            className="text-[#EADDCC]/40 text-sm font-light italic text-center mb-14 max-w-sm"
            style={{ fontFamily: "'Cormorant Garamond', serif", lineHeight: 1.9, letterSpacing: '0.05em' }}
          >
            Move your mouse to leave a mark.
            <br />
            Click to release the beast.
          </p>
          <button
            onClick={handleEnter}
            className="group relative px-12 py-4 border border-[#EADDCC]/50 text-[#EADDCC] text-xs tracking-widest overflow-hidden cursor-pointer whitespace-nowrap transition-all duration-500 hover:border-[#EADDCC]"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.4em' }}
          >
            <span className="absolute inset-0 bg-[#EADDCC] transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
            <span className="relative group-hover:text-black transition-colors duration-500">ENTER</span>
          </button>
        </div>

        {/* ── MAIN INTERACTIVE UI ── */}
        {entered && (
          <div className="absolute inset-0 z-20 flex flex-col items-center justify-between py-10 pointer-events-none">
            {/* Top bar */}
            <div className="w-full flex items-center justify-between px-10 pointer-events-auto">
              <Link to="/" className="cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/79bbd12133c9c5c423ad9964f66c8be6/59cfd97b3dc3ca96c63739e63b4f628a.jpeg"
                  alt="Gucci Beauty"
                  className="h-9 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-300"
                />
              </Link>
              <Link
                to="/"
                className="w-9 h-9 flex items-center justify-center border border-[#EADDCC]/25 text-[#EADDCC]/50 hover:text-[#EADDCC] hover:border-[#EADDCC]/60 transition-all duration-300 cursor-pointer"
                title="Exit"
              >
                <i className="ri-close-line text-sm" />
              </Link>
            </div>

            {/* Centre content */}
            <div className="flex flex-col items-center gap-2 pointer-events-auto">
              <div
                className="mb-8 text-center transition-all duration-1000"
                style={{ opacity: hint ? 1 : 0 }}
              >
                <p
                  className="text-[#EADDCC]/30 text-xs italic"
                  style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.15em' }}
                >
                  move · click · feel
                </p>
              </div>
              <InstinctQuote />
            </div>

            {/* Bottom label + scroll hint */}
            <div className="flex flex-col items-center gap-3 pointer-events-none">
              <p
                className="text-[#EADDCC]/20 text-xs tracking-widest"
                style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.4em', fontSize: '10px' }}
              >
                GUCCI BEAUTY — THE BEAST WITHIN
              </p>
              <div className="mt-4 flex flex-col items-center gap-1.5">
                <p
                  className="text-[#EADDCC]/18 text-xs"
                  style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: '0.3em', fontSize: '10px' }}
                >
                  SCROLL TO TRY ON
                </p>
                <div className="w-px h-6 bg-gradient-to-b from-[#EADDCC]/20 to-transparent" />
              </div>
            </div>
          </div>
        )}

        {/* Vignette overlay */}
        <div
          className="absolute inset-0 z-5 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.65) 100%)',
          }}
        />
      </div>

      {/* ── LIPSTICK TRY-ON SECTION ── */}
      <LipstickTryOn />
      <HomeFooter />
    </>
  );
}
